import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { Bell, Check, AlertCircle, Info, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from '../../components/ui';
import './Notifications.css';

const Notifications = () => {
  const { request } = useApi();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  // ── Fetch Notifications ─────
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await request('get', '/api/notifications');
      setNotifications(response || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // ── Mark as Read ────
  const markAsRead = async (id) => {
    try {
      await request('put', `/api/notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // ── Mark All as Read ──────────
  const markAllAsRead = async () => {
    try {
      await request('put', '/api/notifications/read-all');
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true }))
      );
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // ── Clear All ─────────────────────────────────────────────
  const clearAll = async () => {
    if (!window.confirm('Are you sure you want to clear all notifications?')) return;
    try {
      await request('delete', '/api/notifications');
      setNotifications([]);
    } catch (error) {
      console.error('Failed to clear all:', error);
    }
  };

  // ── Get Icon ──────────────────────────────────────────────
  const getIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'success': return <CheckCircle className="w-5 h-5" />;
      case 'error': return <XCircle className="w-5 h-5" />;
      case 'warning': return <AlertCircle className="w-5 h-5" />;
      default: return <Info className="w-5 h-5" />;
    }
  };

  // ── Get Color ─────────────────────────────────────────────
  const getColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'success': return '#10B981';
      case 'error': return '#EF4444';
      case 'warning': return '#F59E0B';
      default: return '#2563EB';
    }
  };

  const getBgColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'success': return '#F0FDF4';
      case 'error': return '#FEF2F2';
      case 'warning': return '#FFFBEB';
      default: return '#EFF6FF';
    }
  };

  // ── Filters ───────────────────────────────────────────────
  const filteredNotifications = notifications.filter(n => {
    if (selectedFilter === 'unread' && n.read) return false;
    if (selectedFilter === 'read' && !n.read) return false;
    if (selectedType !== 'all' && n.type?.toLowerCase() !== selectedType) return false;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;
  const successCount = notifications.filter(n => n.type?.toLowerCase() === 'success').length;
  const errorCount = notifications.filter(n => n.type?.toLowerCase() === 'error').length;
  const warningCount = notifications.filter(n => n.type?.toLowerCase() === 'warning').length;
  const infoCount = notifications.filter(n => n.type?.toLowerCase() === 'info' || !n.type).length;

  if (loading) {
    return (
      <div className="notifications-loading">
        <Spinner size="lg" />
        <p>Loading notifications...</p>
      </div>
    );
  }

  return (
    <div className="notifications-page">
      {/* ── Back Button ── */}
      <button className="notifications-back" onClick={() => navigate(-1)}>
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      {/* ── Header ── */}
      <div className="notifications-header">
        <div className="notifications-title-section">
          <h1 className="notifications-title">Notifications</h1>
          <p className="notifications-subtitle">
            {unreadCount > 0 
              ? `You have ${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`
              : 'All caught up! No unread notifications'
            }
          </p>
        </div>
        <div className="notifications-actions">
          {notifications.length > 0 && unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllAsRead}>
              Mark all read
            </Button>
          )}
          {notifications.length > 0 && (
            <Button variant="danger" size="sm" onClick={clearAll}>
              Clear all
            </Button>
          )}
        </div>
      </div>

      {/* ── Type Filters ── */}
      <div className="notifications-type-filters">
        <button
          className={`type-filter ${selectedType === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedType('all')}
        >
          All ({notifications.length})
        </button>
        <button
          className={`type-filter ${selectedType === 'success' ? 'active' : ''}`}
          onClick={() => setSelectedType('success')}
        >
          ✅ Success ({successCount})
        </button>
        <button
          className={`type-filter ${selectedType === 'error' ? 'active' : ''}`}
          onClick={() => setSelectedType('error')}
        >
          ❌ Error ({errorCount})
        </button>
        <button
          className={`type-filter ${selectedType === 'warning' ? 'active' : ''}`}
          onClick={() => setSelectedType('warning')}
        >
          ⚠️ Warning ({warningCount})
        </button>
        <button
          className={`type-filter ${selectedType === 'info' ? 'active' : ''}`}
          onClick={() => setSelectedType('info')}
        >
          ℹ️ Info ({infoCount})
        </button>
      </div>

      {/* ── Read/Unread Filters ── */}
      <div className="notifications-filters">
        <button
          className={`filter-btn ${selectedFilter === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('all')}
        >
          All
        </button>
        <button
          className={`filter-btn ${selectedFilter === 'unread' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('unread')}
        >
          Unread ({unreadCount})
        </button>
        <button
          className={`filter-btn ${selectedFilter === 'read' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('read')}
        >
          Read ({notifications.length - unreadCount})
        </button>
      </div>

      {/* ── Notification List ── */}
      <div className="notifications-list">
        {filteredNotifications.length === 0 ? (
          <div className="empty-notifications">
            <Bell size={64} />
            <h3>No notifications</h3>
            <p>
              {selectedFilter === 'unread' 
                ? 'You have no unread notifications'
                : selectedFilter === 'read'
                ? 'You have no read notifications'
                : 'You have no notifications yet'
              }
            </p>
          </div>
        ) : (
          filteredNotifications.map(notification => (
            <div
              key={notification.id}
              className={`notification-item ${!notification.read ? 'unread' : ''}`}
              onClick={() => {
                if (!notification.read) {
                  markAsRead(notification.id);
                }
                if (notification.link) {
                  window.location.href = notification.link;
                }
              }}
            >
              <div
                className="notification-icon"
                style={{ 
                  background: getBgColor(notification.type),
                  color: getColor(notification.type)
                }}
              >
                {getIcon(notification.type)}
              </div>
              <div className="notification-content">
                <p className="notification-message">{notification.message}</p>
                <span className="notification-time">{notification.time || 'Just now'}</span>
              </div>
              {!notification.read && <span className="unread-dot" />}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Notifications;